import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Globe } from 'lucide-react';
import { useScheduler } from '@/contexts/SchedulerContext';
import { formatTimezoneOffset } from '@/utils/timezone-utils';

interface CurrentTimezoneSelectorProps {
  selectedTimezone: string;
  onTimezoneChange: (timezone: string) => void;
}

export const CurrentTimezoneSelector: React.FC<CurrentTimezoneSelectorProps> = ({
  selectedTimezone,
  onTimezoneChange,
}) => {
  const { settings } = useScheduler();

  // Check if timezone feature is enabled
  const isTimezoneEnabled = settings.enableTimezones ?? false;

  // Get available timezones from admin settings
  // Only show timezones that admin has configured in Settings > Administrator Settings > Available Timezones
  const availableTimezones = useMemo(() => {
    return settings.availableTimezones || [];
  }, [settings.availableTimezones]);

  // Don't render if timezone feature is not enabled
  if (!isTimezoneEnabled) {
    return null;
  }

  // Don't render if only one timezone is available
  if (availableTimezones.length <= 1) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <Globe className="h-4 w-4 text-muted-foreground" />
      <Select value={selectedTimezone} onValueChange={onTimezoneChange}>
        <SelectTrigger className="w-[200px] h-8">
          <SelectValue>
            {selectedTimezone} ({formatTimezoneOffset(selectedTimezone)})
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[240px]">
          {availableTimezones.map((timezone) => (
            <SelectItem key={timezone} value={timezone}>
              <div className="flex items-center justify-between w-full gap-4">
                <span>{timezone}</span>
                <span className="text-xs text-muted-foreground">
                  {formatTimezoneOffset(timezone)}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
